import React from 'react';
import styled from 'styled-components';
import type { Locale } from '@peloton/internationalize/models/locale';
import { gray1 } from '@engage/colors';
import type { ClassLanguage } from '@engage/settings';
import { spaces } from '@engage/styles';
import { header6Small } from '@engage/typography';
import { SettingsCopy, useAria } from '@members/copy';
import { Aria } from '@members/copy/models.generated/Aria';

import { Settings } from '@members/copy/models.generated/Settings';
import { Radio } from './inputs';
import { SubtitleOption } from './SubtitleOption';

export const SubtitleOptions: React.FC<React.PropsWithChildren<Props>> = ({
  language,
  displayAllClasses,
  displayOnlySubtitles,
  toggleSubtitle,
}) => {
  const allAria = useAria(Aria.All);
  const subtitlesAria = useAria(Aria.Subtitles);

  return (
    <Container data-test-id="subtitle-options-container">
      <SelectAll htmlFor={`${language.locale}-all`}>
        <SettingsCopy id={Settings.LanguagesAll} />
        <RadioContainer>
          <Radio
            id={`${language.locale}-all`}
            data-test-id="all-radio"
            checked={language.displayAllClasses}
            onChange={displayAllClasses}
            name={language.locale}
            aria-label={allAria}
            type="radio"
          />
        </RadioContainer>
      </SelectAll>
      {language.captions.length > 0 ? (
        <>
          <SelectSubtitles htmlFor={`${language.locale}-subtitles`}>
            <SettingsCopy id={Settings.Subtitles} />
            <RadioContainer>
              <Radio
                id={`${language.locale}-subtitles`}
                data-test-id="subtitle-radio"
                checked={!language.displayAllClasses}
                onChange={displayOnlySubtitles}
                name={language.locale}
                aria-label={subtitlesAria}
                type="radio"
              />
            </RadioContainer>
          </SelectSubtitles>
          {!language.displayAllClasses
            ? language.captions.map(caption => (
                <SubtitleOption
                  checkboxId={`${language.locale}-${caption.locale}`}
                  key={caption.locale}
                  caption={caption}
                  toggleSubtitle={toggleSubtitle}
                />
              ))
            : null}
        </>
      ) : null}
    </Container>
  );
};

const RadioContainer = styled.div`
  width: 20px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${spaces.small}px;
`;

const SelectAll = styled.label`
  height: 50px;
  width: 100%;
  background-color: ${gray1};
  border-radius: ${spaces.xxSmall}px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 ${spaces.medium}px;
  cursor: pointer;
  ${header6Small}
`;

const SelectSubtitles = styled(SelectAll)`
  margin-top: ${spaces.small}px;

  &:not(:last-child) {
    border-radius: ${spaces.xxSmall}px ${spaces.xxSmall}px 0 0;
  }
`;

type Props = {
  language: ClassLanguage;
  displayAllClasses: () => void;
  displayOnlySubtitles: () => void;
  toggleSubtitle: (subtitleLocale: Locale) => void;
};
